import { useMemo } from 'react'

import { EyeInvisible, EyeVisible, Pencil } from '@factorialco/f0-react/icons/app'
import { DocumentsSignatureStatusEnum } from 'generated/resources/graphql'

import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { documentSignatureStatusLabel } from 'modules/documents/lib/Documents'

import type { DocumentNode } from './types'

const signatureStatusToVariant: Record<
  DocumentsSignatureStatusEnum,
  'neutral' | 'info' | 'positive' | 'warning' | 'critical'
> = {
  pending: 'warning',
  partially_signed: 'info',
  completed: 'positive',
  declined: 'critical',
  bounced_email: 'critical',
  cancelled: 'neutral',
  error: 'critical',
  expired: 'critical',
}

export function useColumns() {
  const session = useCurrentSession()

  const columns = useMemo(
    () => [
      {
        label: '',
        width: 45,
        render: (item: DocumentNode) => {
          if (!item.contentType) return { type: 'text' as const, value: undefined }

          return {
            type: 'file' as const,
            value: {
              name: item.filename,
              type: item.extension ?? item.contentType.split('/').pop() ?? '',
            },
          }
        },
      },
      {
        label: i18n.t('documents.employee.list.filename'),
        sorting: 'filename' as const,
        width: 400,
        render: (item: DocumentNode) => ({
          type: 'text' as const,
          value: item.filename,
        }),
      },
      {
        label: i18n.t('documents.employee.list.employee'),
        render: (item: DocumentNode) => {
          const employee = item.employee
          if (!employee?.fullName) {
            return { type: 'text' as const, value: '-' }
          }

          return {
            type: 'person' as const,
            value: {
              firstName: employee.fullName,
              lastName: '',
              src: employee.access?.avatar ? buildUrl(employee.access.avatar) : undefined,
            },
          }
        },
      },
      {
        label: i18n.t('documents.employee.list.folder'),
        render: (item: DocumentNode) => ({
          type: 'folder' as const,
          value: item.folder ? { name: item.folder.name } : undefined,
        }),
      },
      {
        label: i18n.t('documents.employee.list.created_at'),
        sorting: 'createdAt' as const,
        width: 150,
        render: (item: DocumentNode) => ({
          type: 'date' as const,
          value: dateBackendDateTimeToUI(item.createdAt),
        }),
      },
      {
        label: i18n.t('documents.employee.list.signature_status'),
        render: (item: DocumentNode) => {
          if (!item.signatureStatus) {
            return { type: 'text' as const, value: '-' }
          }

          return {
            type: 'status' as const,
            value: {
              label: documentSignatureStatusLabel(item.signatureStatus),
              status: signatureStatusToVariant[item.signatureStatus] ?? 'neutral',
            },
          }
        },
      },
      {
        label: ' ',
        width: 45,
        render: (item: DocumentNode) => {
          const currentOwned = item.employee ? session.employee?.id === item.employee.id : false
          const isVisible = currentOwned || item.public

          return {
            type: 'icon' as const,
            value: {
              icon: isVisible ? EyeVisible : EyeInvisible,
              label: isVisible
                ? i18n.t('documents.employee.filters.visible')
                : i18n.t('documents.employee.filters.hidden'),
            },
          }
        },
      },
      {
        label: ' ',
        width: 45,
        render: (item: DocumentNode) => {
          if (item.status !== 'to_fill') {
            return { type: 'text' as const, value: undefined }
          }

          return {
            type: 'icon' as const,
            value: {
              icon: Pencil,
              label: i18n.t('documents.employee.list.to_fill'),
            },
          }
        },
      },
    ],
    [session.employee?.id]
  )

  return {
    sortings: {
      filename: {
        label: i18n.t('documents.employee.list.filename'),
      },
      createdAt: {
        label: i18n.t('documents.employee.list.created_at'),
      },
    },
    defaultSortings: {
      field: 'createdAt' as const,
      order: 'desc' as const,
    },
    columns,
  }
}
